import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { magToast } from '../components/toaster.js';
import { WorkspaceNav } from '../components/workspace-nav.js';
import { deleteWorkspaceApi, type MeResponse, updateWorkspaceNameApi } from '../lib/api.js';
import { signOut } from '../lib/auth-client.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// the team access design doc (CommitMind) — owner-only workspace identity surface.
// First settings here: rename. Future: transfer ownership, delete
// workspace, optional logo/avatar.

export const Route = createFileRoute('/settings/workspace')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    if (me.workspaceRole !== 'owner') {
      // Members/admins can't see this surface. Bounce to /brands so
      // the URL never resolves with the page rendered for them.
      throw redirect({ to: '/brands' });
    }
    return { me };
  },
  loader: ({ context }) => context as { me: MeResponse },
  component: WorkspaceSettingsPage,
});

function WorkspaceSettingsPage() {
  const { t } = useLingui();
  const { me } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();

  const [name, setName] = useState(me.workspaceName ?? '');
  const [saving, setSaving] = useState(false);

  // Resync if the source-of-truth name updates outside this form
  // (e.g. another tab renamed and the cache picked it up).
  useEffect(() => {
    if (me.workspaceName) setName(me.workspaceName);
  }, [me.workspaceName]);

  const dirty = name.trim() !== (me.workspaceName ?? '').trim() && name.trim().length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      const updated = await updateWorkspaceNameApi(name.trim());
      await refreshQueries(queryClient, ['me']);
      await router.invalidate();
      const newName = updated.name.toLowerCase();
      magToast.success(t`renamed.`, t`now showing as ${newName}.`);
    } catch (err) {
      magToast.error("couldn't rename.", founderErrorCopy(err, 'try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <WorkspaceNav />
      <section className="mx-auto max-w-[720px] px-7 pb-16 pt-8">
        <header>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>workspace</Trans>
          </p>
          <h1 className="mt-2 font-display text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
            <Trans>workspace settings.</Trans>
          </h1>
          <p className="mt-3 max-w-[600px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>
              the name shows up in the nav, on invite emails, and on the team page. owners only.
            </Trans>
          </p>
        </header>

        <article className="mt-7 rounded-2xl border border-[var(--line)] bg-white p-5">
          <h2 className="font-display text-[18px] font-bold tracking-[-0.01em]">
            <Trans>name</Trans>
          </h2>
          <p className="mt-1 text-[13px] text-[var(--ink)]/65">
            <Trans>keep it short — it's prefixed with "↘" in every nav surface.</Trans>
          </p>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
            <label className="field">
              <span className="field-label">
                <Trans>workspace name</Trans>
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder={t`luis's workspace`}
              />
            </label>
            <div className="flex justify-end">
              <button type="submit" disabled={!dirty || saving} className="btn disabled:opacity-50">
                {saving ? t`saving…` : t`save`}
                {!saving ? <span className="btn-arrow">→</span> : null}
              </button>
            </div>
          </form>
        </article>

        <DangerZone workspaceName={me.workspaceName ?? ''} />
      </section>
    </main>
  );
}

/** Hard-delete the workspace + every user who is a member of it.
 *  Owner-only (enforced server-side; the page itself is owner-gated
 *  via beforeLoad). Type-to-confirm so an accidental click can't
 *  bring down the whole tenant. After success: sign-out the
 *  local session and redirect to /login. */
function DangerZone({ workspaceName }: { workspaceName: string }) {
  const { t } = useLingui();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const target = workspaceName.trim();
  const matches = typed.trim() === target && target.length > 0;

  const handleDelete = async () => {
    if (!matches || deleting) return;
    setDeleting(true);
    try {
      await deleteWorkspaceApi();
      // Best-effort sign-out — the server already nuked the user row,
      // so this is purely to clear the local cookie + better-auth's
      // client cache. Errors here are ignored; the redirect happens
      // regardless.
      try {
        await signOut();
      } catch {
        // ignore — the session is dead on the server anyway.
      }
      window.location.href = '/login';
    } catch (err) {
      magToast.error("couldn't delete the workspace.", founderErrorCopy(err, 'try again.'));
      setDeleting(false);
    }
  };

  return (
    <article className="mt-10 rounded-2xl border-[1.5px] border-[var(--pulse)]/35 bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
        {'↘'} <Trans>danger zone</Trans>
      </p>
      <h2 className="mt-2 font-display text-[18px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        <Trans>delete this workspace</Trans>
      </h2>
      <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          permanently removes the workspace, every brand inside it, every campaign, every memo, and
          every user who's a member. <span className="font-bold">this cannot be undone.</span>{' '}
          you'll be signed out.
        </Trans>
      </p>
      {!confirmOpen ? (
        <div className="mt-5 flex justify-end">
          <button type="button" onClick={() => setConfirmOpen(true)} className="btn btn-pulse">
            <Trans>delete workspace</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 p-4">
          <p className="text-[12.5px] leading-[1.5] text-[var(--ink)]/85">
            <Trans>
              type{' '}
              <code className="rounded bg-white px-1.5 py-0.5 font-mono text-[12px]">{target}</code>{' '}
              below to confirm. this can't be undone.
            </Trans>
          </p>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={target}
            disabled={deleting}
            // biome-ignore lint/a11y/noAutofocus: opens via explicit click; typing IS the next intended action.
            autoFocus
            className="mt-3 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-[14px] outline-none focus:border-[var(--pulse)]"
          />
          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setConfirmOpen(false);
                setTyped('');
              }}
              disabled={deleting}
              className="link-quiet text-[12px] disabled:opacity-60"
            >
              <Trans>cancel</Trans>
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!matches || deleting}
              className="btn btn-pulse-solid disabled:opacity-50"
            >
              {deleting ? t`deleting…` : t`delete forever`}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
